import * as React from 'react'
import {useSearchParams} from 'react-router'

/**
 * Hook similar to `React.useState` which stores the value in query parameter.
 *
 * Calling returned setter with `null` value will delete the query parameter.
 * @param queryParameterName Query parameter name where state should be stored.
 * @returns Tuple `[query parameter value, setter for query parameter]`
 */
export function useQueryParameterState(queryParameterName: string): [string | null, (value: string | null) => void]

/**
 * Hook similar to `React.useState` which stores the value in query parameter.
 *
 * Calling returned setter with `null` value will delete the query parameter.
 * @param queryParameterName Query parameter name where state should be stored.
 * @param initialValue Initial state value if query parameter does not exist yet.
 * @returns Tuple `[query parameter value, setter for query parameter]`
 */
export function useQueryParameterState(
    queryParameterName: string,
    initialValue?: string | undefined
): [string | null, (value: string | null) => void]

/**
 * Hook similar to `React.useState` which stores the value in query parameter.
 * @param queryParameterName Query parameter name where state should be stored.
 * @param initialValue Initial state value if query parameter does not exist yet.
 * @returns Tuple `[query parameter value, setter for query parameter]`
 */
export function useQueryParameterState(
    queryParameterName: string,
    initialValue: string
): [string, (value: string) => void]

export function useQueryParameterState(
    queryParameterName: string,
    initialValue?: string
): [string | null, ((value: string | null) => void) | ((value: string) => void)] {
    const [searchParams, setSearchParams] = useSearchParams()

    const setQueryParameters = React.useCallback(
        (newValue: string | null) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (newValue === null) {
                        next.delete(queryParameterName)
                    } else {
                        next.set(queryParameterName, newValue)
                    }
                    return next
                },
                {replace: true}
            )
        },
        [setSearchParams, queryParameterName]
    )

    React.useEffect(() => {
        if (typeof initialValue !== 'undefined' && !searchParams.has(queryParameterName)) {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set(queryParameterName, initialValue)
                    return next
                },
                {replace: true}
            )
        }
    }, [searchParams, queryParameterName, initialValue, setSearchParams])

    return [searchParams.get(queryParameterName), setQueryParameters]
}
